/**
 * @fileoverview File upload component with drag-and-drop support
 * @module ctl-file-upload
 * @version 1.0.0
 * 
 * @description
 * A file upload component that supports drag-and-drop, multiple files,
 * file type validation, and preview capabilities.
 * 
 * @example
 * <!-- Basic usage -->
 * <ctl-file-upload label="Upload Files"></ctl-file-upload>
 * 
 * @example
 * <!-- With restrictions -->
 * <ctl-file-upload 
 *   label="Upload Images" 
 *   accept="image/*"
 *   multiple
 *   max-size="5242880"
 * ></ctl-file-upload>
 */

import { logger } from '../../../utils/logger.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui, -apple-system, Segoe UI, Roboto, Arial;
    --ctl-radius: var(--border-radius-md, 8px);

    --ctl-color-focus: var(--ctl-color-brand);

    --ctl-pad: var(--spacing-md, 16px);
    --ctl-gap: var(--spacing-xs, 8px);

    display: block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-1, var(--spacing-xxs, 4px));
  }

  .label {
    font-size: var(--font-size-sm, 0.875rem);
    font-weight: var(--font-weight-normal, 400);
    color: var(--ctl-color-text);
  }

  .dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--ctl-gap);
    padding: var(--ctl-pad) calc(var(--ctl-pad) * 2);
    border: var(--border-width-thick, 2px) dashed var(--ctl-color-border);
    border-radius: var(--ctl-radius);
    background: var(--ctl-color-surface);
    cursor: pointer;
    transition: border-color var(--transition-fast, 150ms ease-in-out), background var(--transition-fast, 150ms ease-in-out), opacity var(--transition-fast, 150ms ease-in-out);
    min-height: var(--container-xs, 120px);
  }

  .dropzone:hover {
    border-color: var(--ctl-color-focus);
    background: color-mix(in srgb, var(--ctl-color-focus) 2%, transparent);
  }

  .dropzone.dragover {
    border-color: var(--ctl-color-focus);
    background: color-mix(in srgb, var(--ctl-color-focus) 5%, transparent);
    border-style: solid;
  }

  :host([disabled]) .dropzone {
    opacity: var(--opacity-60, 0.6);
    cursor: not-allowed;
    background: var(--ctl-color-surface-2, color-mix(in srgb, var(--color-black) 2%, transparent));
  }

  :host([disabled]) .dropzone:hover {
    border-color: var(--ctl-color-border);
    background: var(--ctl-color-surface-2, color-mix(in srgb, var(--color-black) 2%, transparent));
  }

  .dropzone-icon {
    font-size: var(--font-size-xxl, 2rem);
    color: var(--ctl-color-muted);
  }

  .dropzone-text {
    text-align: center;
  }

  .dropzone-primary {
    font-size: var(--font-size-sm, 0.875rem);
    color: var(--ctl-color-text);
    font-weight: var(--font-weight-normal, 400);
  }

  .dropzone-secondary {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-muted);
    margin-top: var(--ctl-space-1, var(--spacing-xxs, 4px));
  }

  input[type="file"] {
    display: none;
  }

  .file-list {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-gap);
    margin-top: var(--ctl-gap);
  }

  .file-item {
    display: flex;
    align-items: center;
    gap: var(--ctl-gap);
    padding: var(--ctl-gap) var(--ctl-space-3, var(--spacing-sm, 12px));
    border-radius: calc(var(--ctl-radius) / 2);
    background: var(--ctl-color-surface-2, color-mix(in srgb, var(--color-black) 2%, transparent));
    border: var(--border-width-thin, 1px) solid var(--ctl-color-border);
  }

  .file-icon {
    font-size: var(--font-size-lg, 1.125rem);
    color: var(--ctl-color-muted);
  }

  .file-info {
    flex: 1;
    min-width: 0;
  }

  .file-name {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .file-size {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-muted);
    margin-top: calc(var(--ctl-space-1, var(--spacing-xxs, 4px)) / 2);
  }

  .file-remove {
    padding: var(--ctl-space-1, var(--spacing-xxs, 4px)) var(--ctl-space-2, var(--spacing-xs, 8px));
    border: none;
    background: transparent;
    color: var(--ctl-color-danger);
    cursor: pointer;
    border-radius: var(--ctl-radius-sm, var(--border-radius-sm, 4px));
    font-size: var(--font-size-xs, 0.75rem);
    transition: background var(--transition-fast, 150ms ease-in-out);
  }

  .file-remove:hover {
    background: color-mix(in srgb, var(--ctl-color-danger) 10%, transparent);
  }

  .help {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-muted);
  }

  .error {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-danger);
  }

  .error:empty {
    display: none;
  }
</style>

<div class="container">
  <label class="label" part="label"></label>
  <div class="dropzone" part="dropzone">
    <div class="dropzone-icon">📁</div>
    <div class="dropzone-text">
      <div class="dropzone-primary">Click to upload or drag and drop</div>
      <div class="dropzone-secondary"></div>
    </div>
    <input type="file" part="input" />
  </div>
  <div class="file-list" part="file-list"></div>
  <div class="help" part="help"></div>
  <div class="error" part="error"></div>
</div>
`;

/**
 * File Upload Web Component
 * 
 * @class CtlFileUpload
 * @extends HTMLElement
 * 
 * @attr {string} label - Label text for the file upload
 * @attr {string} accept - Accepted file types (e.g., "image/*", ".pdf,.doc")
 * @attr {boolean} multiple - Whether multiple files can be selected
 * @attr {number} max-size - Maximum file size in bytes
 * @attr {number} max-files - Maximum number of files allowed
 * @attr {string} help - Help text displayed below the dropzone
 * @attr {string} error - Error message displayed below the dropzone
 * @attr {boolean} disabled - Whether the file upload is disabled
 * @attr {string} name - Name attribute for form submission
 * 
 * @fires ctl-change - Dispatched when files are added or removed
 * @fires ctl-change.detail.files - Array of selected File objects
 * @fires ctl-error - Dispatched when file validation fails
 * @fires ctl-error.detail.message - Error message
 * @fires ctl-error.detail.file - The file that caused the error
 * 
 * @csspart label - The label element
 * @csspart dropzone - The dropzone container
 * @csspart input - The file input element
 * @csspart file-list - The list of selected files
 * @csspart help - The help text element
 * @csspart error - The error message element
 * 
 * @cssprop --ctl-color-text - Text color
 * @cssprop --ctl-color-muted - Muted text color
 * @cssprop --ctl-color-border - Border color
 * @cssprop --ctl-color-surface - Background color
 * @cssprop --ctl-color-focus - Focus/hover state color
 * @cssprop --ctl-color-danger - Error state color
 * @cssprop --ctl-radius - Border radius
 */
export class CtlFileUpload extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    this._input = this.shadowRoot.querySelector('input');
    this._dropzone = this.shadowRoot.querySelector('.dropzone');
    this._label = this.shadowRoot.querySelector('.label');
    this._help = this.shadowRoot.querySelector('.help');
    this._error = this.shadowRoot.querySelector('.error');
    this._fileList = this.shadowRoot.querySelector('.file-list');
    this._dropzoneSecondary = this.shadowRoot.querySelector('.dropzone-secondary');
    
    // Validate critical elements
    if (!this._input || !this._dropzone) {
      logger.error('ctl-file-upload: Required elements not found in shadow DOM');
      this._isValid = false;
      return;
    }
    
    this._isValid = true;
    this._files = [];
    
    // Bind methods once in constructor to maintain reference for cleanup
    this._boundHandleClick = this._handleClick.bind(this);
    this._boundHandleDragOver = this._handleDragOver.bind(this);
    this._boundHandleDragLeave = this._handleDragLeave.bind(this);
    this._boundHandleDrop = this._handleDrop.bind(this);
    this._boundHandleFileSelect = this._handleFileSelect.bind(this);
  }

  static get observedAttributes() {
    return ['label', 'accept', 'multiple', 'max-size', 'max-files', 
            'help', 'error', 'disabled', 'name'];
  }

  connectedCallback() {
    if (!this._isValid) return; // Skip if elements not found
    
    this._dropzone.addEventListener('click', this._boundHandleClick);
    this._dropzone.addEventListener('dragover', this._boundHandleDragOver);
    this._dropzone.addEventListener('dragleave', this._boundHandleDragLeave);
    this._dropzone.addEventListener('drop', this._boundHandleDrop);
    this._input.addEventListener('change', this._boundHandleFileSelect);
    this._render();
  }

  disconnectedCallback() {
    if (!this._isValid) return; // Skip if elements not found
    
    this._dropzone?.removeEventListener('click', this._boundHandleClick);
    this._dropzone?.removeEventListener('dragover', this._boundHandleDragOver);
    this._dropzone?.removeEventListener('dragleave', this._boundHandleDragLeave);
    this._dropzone?.removeEventListener('drop', this._boundHandleDrop);
    this._input?.removeEventListener('change', this._boundHandleFileSelect);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this._render();
    }
  }

  _handleClick(e) {
    if (this.hasAttribute('disabled')) return;
    if (!this._input) return; // Guard against missing input
    if (e.target === this._input) return;
    this._input.click();
  }

  _handleDragOver(e) {
    if (this.hasAttribute('disabled')) return;
    e.preventDefault();
    this._dropzone.classList.add('dragover');
  }

  _handleDragLeave(e) {
    if (this.hasAttribute('disabled')) return;
    e.preventDefault();
    this._dropzone.classList.remove('dragover');
  }

  _handleDrop(e) {
    if (this.hasAttribute('disabled')) return;
    e.preventDefault();
    this._dropzone.classList.remove('dragover');
    
    const files = Array.from(e.dataTransfer.files);
    this._addFiles(files);
  }

  _handleFileSelect(e) {
    const files = Array.from(e.target.files);
    this._addFiles(files);
  }

  _addFiles(newFiles) {
    const maxFiles = parseInt(this.getAttribute('max-files')) || Infinity;
    const maxSize = parseInt(this.getAttribute('max-size')) || Infinity;
    const accept = this.getAttribute('accept');
    
    // Filter and validate files
    for (const file of newFiles) {
      // Check max files
      if (this._files.length >= maxFiles) {
        this._dispatchError(`Maximum ${maxFiles} file(s) allowed`, file);
        break;
      }
      
      // Check file size
      if (file.size > maxSize) {
        const sizeMB = (maxSize / 1024 / 1024).toFixed(1);
        this._dispatchError(`File size exceeds ${sizeMB}MB`, file);
        continue;
      }
      
      // Check file type
      if (accept && !this._matchesAccept(file, accept)) {
        this._dispatchError(`File type not accepted`, file);
        continue;
      }
      
      this._files.push(file);
    }
    
    this._renderFileList();
    this._dispatchChange();
  }

  _matchesAccept(file, accept) {
    const patterns = accept.split(',').map(p => p.trim());
    return patterns.some(pattern => {
      if (pattern.startsWith('.')) {
        return file.name.toLowerCase().endsWith(pattern.toLowerCase());
      }
      if (pattern.includes('/*')) {
        const type = pattern.split('/')[0];
        return file.type.startsWith(type);
      }
      return file.type === pattern;
    });
  }

  _removeFile(index) {
    this._files.splice(index, 1);
    this._renderFileList();
    this._dispatchChange();
  }

  _formatSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 10) / 10 + ' ' + sizes[i];
  }

  _renderFileList() {
    if (!this._fileList) return; // Guard against missing element
    
    this._fileList.innerHTML = '';
    
    this._files.forEach((file, index) => {
      const item = document.createElement('div');
      item.className = 'file-item';
      item.innerHTML = `
        <div class="file-icon">📄</div>
        <div class="file-info">
          <div class="file-name">${file.name}</div>
          <div class="file-size">${this._formatSize(file.size)}</div>
        </div>
        <button class="file-remove" data-index="${index}">Remove</button>
      `;
      
      const removeBtn = item.querySelector('.file-remove');
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._removeFile(index);
      });
      
      this._fileList.appendChild(item);
    });
  }

  _dispatchChange() {
    this.dispatchEvent(new CustomEvent('ctl-change', {
      bubbles: true,
      composed: true,
      detail: {
        files: this._files
      }
    }));
  }

  _dispatchError(message, file) {
    this.dispatchEvent(new CustomEvent('ctl-error', {
      bubbles: true,
      composed: true,
      detail: {
        message,
        file
      }
    }));
  }

  _render() {
    if (!this._isValid) return; // Skip if elements not found
    
    // Label
    const label = this.getAttribute('label');
    if (this._label) {
      if (label) {
        this._label.textContent = label;
        this._label.style.display = 'block';
      } else {
        this._label.style.display = 'none';
      }
    }

    // Accept
    const accept = this.getAttribute('accept');
    if (this._input && accept) {
      this._input.accept = accept;
    }

    // Multiple
    if (this._input) {
      this._input.multiple = this.hasAttribute('multiple');
    }

    // Name
    const name = this.getAttribute('name');
    if (this._input && name) {
      this._input.name = name;
    }

    // Help text and restrictions
    const help = this.getAttribute('help');
    const maxSize = this.getAttribute('max-size');
    const maxFiles = this.getAttribute('max-files');
    
    let restrictionsText = [];
    if (accept) restrictionsText.push(accept);
    if (maxSize) restrictionsText.push(`Max ${(parseInt(maxSize) / 1024 / 1024).toFixed(1)}MB`);
    if (maxFiles) restrictionsText.push(`Up to ${maxFiles} files`);
    
    if (this._dropzoneSecondary && restrictionsText.length > 0) {
      this._dropzoneSecondary.textContent = restrictionsText.join(' • ');
    }

    if (this._help) {
      if (help) {
        this._help.textContent = help;
        this._help.style.display = 'block';
      } else {
        this._help.style.display = 'none';
      }
    }

    // Error message
    const error = this.getAttribute('error');
    if (this._error) {
      if (error) {
        this._error.textContent = error;
        this._error.style.display = 'block';
      } else {
        this._error.style.display = 'none';
      }
    }
  }

  /**
   * Get the selected files
   * @returns {File[]} Array of selected files
   */
  get files() {
    return this._files;
  }

  /**
   * Clear all selected files
   */
  clear() {
    this._files = [];
    this._input.value = '';
    this._renderFileList();
    this._dispatchChange();
  }
}

if (!customElements.get("ctl-file-upload")) {
  customElements.define("ctl-file-upload", CtlFileUpload);
}
